/**
 * @module app.map
 */
import {SWISSTOPO_PIXELS_PER_METER} from './constants.js';
import {getConstrainedResolution, lonLatToMapProjection, lv95ToMapProjection} from './utils.js';
import {proj as proj2056} from '@geoblocks/proj/src/EPSG_2056.js';
import {getCenter} from 'ol/extent.js';
import {transform as olProjTransform} from 'ol/proj.js';


class MapController {
  /**
   * @param {ngeo.statemanager.Location} ngeoLocation ngeo Location service.
   * @param {ngeox.miscDebounce} ngeoDebounce ngeo Debounce service.
   * @ngInject
   */
  constructor(ngeoLocation, ngeoDebounce) {
    /**
     * @export
     * @type {ol.Map}
     */
    this.map;

    /**
     * @private
     * @type {ngeo.statemanager.Location}
     */
    this.ngeoLocation_ = ngeoLocation;

    /**
     * @private
     * @type {ngeox.miscDebounce}
     */
    this.ngeoDebounce_ = ngeoDebounce;
  }

  $onInit() {
    const map = this.map;
    const view = map.getView();
    const map_projection = view.getProjection();

    view.on('propertychange', this.ngeoDebounce_(() => {
      let center = view.getCenter();
      const dest = proj2056;
      console.assert(center);
      center = olProjTransform(center, map_projection, dest);

      const resolution = view.getResolution();
      const params = {
        'resolution': +resolution.toFixed(2),
        'E': Math.round(center[0]),
        'N': Math.round(center[1])
      };
      this.ngeoLocation_.updateParams(params);
    }, 300, /* invokeApply */ true));

    const initialExtent = [461000, 130125, 802500, 247875];

    let resolution = this.ngeoLocation_.getParamAsFloat('resolution');
    const zoom = this.ngeoLocation_.getParamAsFloat('zoom');
    const scale = this.ngeoLocation_.getParamAsFloat('scale');

    if (!isNaN(zoom)) {
      this.ngeoLocation_.deleteParam('zoom');
    }
    if (!isNaN(scale) && isNaN(resolution)) {
      resolution = scale / SWISSTOPO_PIXELS_PER_METER;
      this.ngeoLocation_.deleteParam('scale');
    }

    const x = this.ngeoLocation_.getParamAsFloat('X');
    const y = this.ngeoLocation_.getParamAsFloat('Y');
    let lon = this.ngeoLocation_.getParam('lon_i');
    let lat = this.ngeoLocation_.getParam('lat_i');
    const e = this.ngeoLocation_.getParamAsFloat('E');
    const n = this.ngeoLocation_.getParamAsFloat('N');
    let center;
    if (!isNaN(x) && !isNaN(y)) {
      // coordinates in EPSG:21781
      center = [x, y];
      this.ngeoLocation_.deleteParam('X');
      this.ngeoLocation_.deleteParam('Y');
    } else if (lon !== undefined && lat !== undefined) {
      // lon_i / lat_i are WGS84 coordinates with no "." sign.

      // Because longitude for switzerland range from ~5.9 to ~10.4;
      // 1 or 2 digits represents the "whole number" and the rest is
      // the decimal fraction.
      // Example: 8051260 -> 1 digit  -> lon:  8.051260
      //         10079325 -> 2 digits -> lon: 10.079325
      const digits = (lon.substr(0, 1) == 1) ? 2 : 1;
      lon = parseFloat(lon.substr(0, digits) + '.' + lon.substr(digits, lon.length - digits));

      // no need to check the number of digits, it's always 2.
      // Example: 47391355 -> 2 digits -> lat: 47.391355
      lat = parseFloat(lat.substr(0, 2) + '.' + lat.substr(2, lat.length - 2));

      if (!isNaN(lon) && !isNaN(lat)) {
        center = lonLatToMapProjection([lon, lat]);
      }

      this.ngeoLocation_.deleteParam('lon_i');
      this.ngeoLocation_.deleteParam('lat_i');
    } else if (!isNaN(e) && !isNaN(n)) {
      // coordinates in EPSG:2056
      center = lv95ToMapProjection([e, n]);
    } else {
      const extent = map_projection.getExtent();
      center = getCenter(extent);
    }

    view.setCenter(center);
    if (!isNaN(zoom)) {
      console.assert(zoom !== undefined);
      view.setZoom(zoom);
    } else if (!isNaN(resolution)) {
      view.setResolution(getConstrainedResolution(view, resolution));
    } else {
      const size = map.getSize();
      if (size) {
        view.fit(initialExtent, {size});
        const resolution = view.getResolution();
        view.setResolution(getConstrainedResolution(view, resolution));
      } else {
        map.once('change:size', function() {
          const size_ = map.getSize();
          console.assert(size_);
          view.fit(initialExtent, {size: size_});
          const resolution = view.getResolution();
          view.setResolution(getConstrainedResolution(view, resolution));
        });
      }
    }
  }
}

/**
 * An application-specific map directive that updates the URL in the browser
 * address bar when the map view changes. It also sets the initial view based
 * on the URL query params at init time.
 *
 * This directive gets a reference to the map instance through the "app-map"
 * attribute.
 *
 * @const {!angular.Component}
 */
export const mapComponent = {
  bindings: {
    'map': '=appMap'
  },
  controller: MapController,
  template: '<div ngeo-map=$ctrl.map></div>'
};

const mapModule = angular.module('appMap', []).component('appMap', mapComponent);

export default mapModule;
